import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Server, Globe, Router, Camera, Monitor, Box, Plus, Wifi, Network,
  CheckCircle2, AlertCircle, Loader2, Power, MoreHorizontal,
} from "lucide-react";
import { useDevices, type Device, type DeviceConnector, type DeviceKind } from "@/lib/queries";
import { AddDeviceWizard } from "@/components/AddDeviceWizard";
import { cn } from "@/lib/utils";

const KIND_ICON: Record<DeviceKind, typeof Server> = {
  linux:        Server,
  cpanel:       Globe,
  mikrotik:     Router,
  hikvision:    Camera,
  windows:      Monitor,
  unifi_ap:     Wifi,
  unifi_switch: Network,
  other:        Box,
};

const CONNECTOR_LABEL: Record<string, string> = {
  ssh:            "SSH",
  filebeat:       "Filebeat",
  zabbix_agent:   "Zabbix",
  netflow:        "NetFlow",
  snmp:           "SNMP",
  api:            "API",
  http_digest:    "HTTP",
  shell_command:  "Shell",
  http_poll:      "HTTP poll",
  log_tail:       "Log tail",
};

const CONNECTOR_TONE: Record<string, string> = {
  active:      "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  configuring: "border-amber-500/40   bg-amber-500/10   text-amber-700   dark:text-amber-400",
  pending:     "border-border         bg-muted          text-muted-foreground",
  failing:     "border-red-500/40     bg-red-500/10     text-red-600     dark:text-red-400",
  disabled:    "border-border         bg-muted/40       text-muted-foreground",
};

function ConnectorChip({ c }: { c: DeviceConnector }) {
  const tone = CONNECTOR_TONE[c.status] ?? CONNECTOR_TONE.pending;
  const Icon =
    c.status === "active"      ? CheckCircle2 :
    c.status === "failing"     ? AlertCircle :
    c.status === "configuring" ? Loader2 : Power;
  return (
    <span
      title={c.last_error ? `${c.kind}: ${c.last_error}` : `${c.kind}: ${c.status}`}
      className={cn("inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium", tone)}
    >
      <Icon className={cn("h-3 w-3", c.status === "configuring" && "animate-spin")} />
      {CONNECTOR_LABEL[c.kind] ?? c.kind}
    </span>
  );
}

function DeviceCard({ device }: { device: Device }) {
  const Icon = KIND_ICON[device.kind] ?? Box;
  const allOk = device.connectors.length > 0 && device.connectors.every((c) => c.status === "active");
  const anyFailing = device.connectors.some((c) => c.status === "failing");
  const noConnectors = device.connectors.length === 0;

  return (
    <Link
      to={`/devices/${device.id}`}
      className="group flex flex-col rounded-lg border border-border bg-card p-4 transition-colors hover:border-primary/30 hover:bg-primary/5"
    >
      <div className="flex items-start gap-3">
        <div className={cn(
          "flex h-11 w-11 shrink-0 items-center justify-center rounded-lg",
          allOk      ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" :
          anyFailing ? "bg-red-500/10 text-red-600 dark:text-red-400" :
                       "bg-primary/10 text-primary",
        )}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold">{device.name}</div>
          {device.role && <div className="truncate text-[11px] text-muted-foreground">{device.role}</div>}
          {!device.role && device.hostname && device.hostname !== device.name && (
            <div className="truncate font-mono text-[11px] text-muted-foreground">{device.hostname}</div>
          )}
        </div>
        <span aria-hidden className="rounded-md p-1 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100">
          <MoreHorizontal className="h-4 w-4" />
        </span>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        {noConnectors && <span className="text-[10px] text-muted-foreground">No connectors yet</span>}
        {device.connectors.map((c, i) => <ConnectorChip key={i} c={c} />)}
      </div>

      <div className="mt-3 flex items-center justify-between text-[10px] text-muted-foreground">
        <span className="font-mono uppercase">{device.kind}</span>
        <span>{device.ssh_user}@{device.hostname ?? device.name}:{device.ssh_port}</span>
      </div>
    </Link>
  );
}

export default function Devices() {
  const [wizardOpen, setWizardOpen] = useState(false);
  const q = useDevices();
  const items = q.data?.items ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Devices</h1>
          <p className="text-sm text-muted-foreground">
            Hosts, routers, cameras, and other endpoints Tyrael watches for your tenant.
            {q.data ? ` ${q.data.total} declared.` : ""}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setWizardOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" /> Add device
        </button>
      </div>

      {q.isLoading && <div className="text-sm text-muted-foreground">Loading…</div>}

      {q.data && items.length === 0 && (
        <div className="rounded-lg border border-dashed border-border bg-muted/20 p-12 text-center">
          <div className="text-sm font-medium">No devices yet</div>
          <p className="mt-1 text-xs text-muted-foreground">
            Click <strong>Add device</strong> to onboard your first host. The wizard walks you through
            authorising Tyrael's SSH key, testing connectivity, and installing the log shipper —
            ~2 minutes total.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        {items.map((d) => <DeviceCard key={d.id} device={d} />)}
      </div>

      <AddDeviceWizard open={wizardOpen} onOpenChange={setWizardOpen} />
    </div>
  );
}
